import { $logger } from '@/boot/logger'

/**
 * @typedef {Object} AssembledDimensions
 * @property {number|null} length - Product length in inches
 * @property {number|null} width - Product width in inches
 * @property {number|null} height - Product height in inches
 */

/**
 * @typedef {Object} ProductStateAssessment
 * @property {'in-box'|'removed'|null} productState
 * @property {'no-assembly-required'|'not-assembled'|'assembled'|null} assemblyState
 * @property {'yes'|'no'|null} canDisassemble
 * @property {AssembledDimensions|null} assembledDimensions
 */

/**
 * @typedef {Object} ItemProductStateAssessmentState
 * @property {ProductStateAssessment} assessment - Assessment data for this item
 * @property {number} currentStep - Current step in assessment flow
 * @property {boolean} isComplete - Whether assessment is complete
 */

/**
 * Customer Return Product State Store
 *
 * Manages per-item product state assessments during the customer return flow.
 * This store handles:
 * - Per-item product state assessment tracking (keyed by lineItemSessionId)
 * - Product packaging state (in-box vs removed)
 * - Assembly state tracking
 * - Disassembly capability assessment
 * - Multi-step form navigation
 *
 * @public API:
 * - State: productStateAssessment (computed), currentStep (computed), totalSteps (computed), activeItemSessionId
 * - Actions: setActiveItem, initializeItemAssessment, getAssessmentForItem, isItemAssessmentComplete, nextStep, prevStep, reset, submitForm
 *
 * @store customerReturnProductState
 */

// ============================================================================
// CONSTANTS
// ============================================================================

/** @type {ProductStateAssessment} */
const DEFAULT_PRODUCT_STATE_ASSESSMENT = {
  productState: null,
  assemblyState: null,
  canDisassemble: null,
  assembledDimensions: null,
}

// ============================================================================
// STORE DEFINITION
// ============================================================================

export const useCustomerReturnProductStateStore = defineStore('customerReturnProductState', () => {
  // ============================================================================
  // STATE
  // ============================================================================

  /**
   * Per-item product state assessments keyed by lineItemSessionId
   * @type {Ref<Record<string, ItemProductStateAssessmentState>>}
   * @private
   */
  const productStateAssessments = ref({})

  /**
   * Currently active item's lineItemSessionId
   * @type {Ref<string|null>}
   * @public
   */
  const activeItemSessionId = ref(null)

  /**
   * Product state assessment data for active item (computed)
   * @type {ComputedRef<ProductStateAssessment>}
   * @public
   */
  const productStateAssessment = computed(() => {
    if (!activeItemSessionId.value) {
      return { ...DEFAULT_PRODUCT_STATE_ASSESSMENT }
    }
    return productStateAssessments.value[activeItemSessionId.value]?.assessment || { ...DEFAULT_PRODUCT_STATE_ASSESSMENT }
  })

  /**
   * Current step in the assessment flow for active item (computed)
   * @type {ComputedRef<number>}
   * @public
   */
  const currentStep = computed(() => {
    if (!activeItemSessionId.value) return 1
    return productStateAssessments.value[activeItemSessionId.value]?.currentStep || 1
  })

  /**
   * Total number of steps in the assessment (dynamic based on product state)
   * @type {ComputedRef<number>}
   * @public
   */
  const totalSteps = computed(() => {
    if (!activeItemSessionId.value) return 4
    const assessment = productStateAssessments.value[activeItemSessionId.value]?.assessment
    if (!assessment) return 4

    // in-box: 1 step only
    if (assessment.productState === 'in-box') return 1

    // removed + not assembled: 2 steps (product state + assembly)
    if (assessment.assemblyState && assessment.assemblyState !== 'assembled') return 2

    // removed + assembled + can disassemble: 3 steps (product state + assembly + disassembly)
    if (assessment.canDisassemble === 'yes') return 3

    // removed + assembled + cannot put back in packaging: 4 steps (includes dimensions)
    return 4
  })

  // ============================================================================
  // ACTIONS (PUBLIC API)
  // ============================================================================

  /**
   * Set the active item for assessment
   * @param {string} lineItemSessionId - Line item session ID
   * @public
   */
  function setActiveItem(lineItemSessionId) {
    if (!lineItemSessionId) {
      $logger.warn('Product state: Cannot set active item without lineItemSessionId')
      return
    }

    activeItemSessionId.value = lineItemSessionId

    // Initialize if doesn't exist
    if (!productStateAssessments.value[lineItemSessionId]) {
      initializeItemAssessment(lineItemSessionId)
    }
  }

  /**
   * Initialize assessment for a specific item
   * @param {string} lineItemSessionId - Line item session ID
   * @public
   */
  function initializeItemAssessment(lineItemSessionId) {
    if (!lineItemSessionId) {
      $logger.warn('Product state: Cannot initialize without lineItemSessionId')
      return
    }

    // Don't reinitialize if already exists
    if (productStateAssessments.value[lineItemSessionId]) {
      return
    }

    productStateAssessments.value[lineItemSessionId] = {
      assessment: { ...DEFAULT_PRODUCT_STATE_ASSESSMENT },
      currentStep: 1,
      isComplete: false,
    }
  }

  /**
   * Get assessment data for a specific item
   * @param {string} lineItemSessionId - Line item session ID
   * @returns {ItemProductStateAssessmentState|null}
   * @public
   */
  function getAssessmentForItem(lineItemSessionId) {
    if (!lineItemSessionId) return null
    return productStateAssessments.value[lineItemSessionId] || null
  }

  /**
   * Check if item's assessment is complete
   * @param {string} lineItemSessionId - Line item session ID
   * @returns {boolean}
   * @public
   */
  function isItemAssessmentComplete(lineItemSessionId) {
    if (!lineItemSessionId) return false
    return productStateAssessments.value[lineItemSessionId]?.isComplete || false
  }

  /**
   * Navigate to next step based on assessment answers
   * Logic: Skip steps based on product state and assembly status
   * Flow:
   * - in-box: Step 1 - final step
   * - removed + not assembled: Step 1 → Step 2 - final step
   * - removed + assembled + can disassemble: Step 1 → Step 2 → Step 3 - final step
   * - removed + assembled + cannot put back in packaging: Step 1 → Step 2 → Step 3 → Step 4 - final step
   * @public
   */
  function nextStep() {
    if (!activeItemSessionId.value) {
      $logger.warn('Product state: Cannot navigate without active item')
      return
    }

    const itemState = productStateAssessments.value[activeItemSessionId.value]
    if (!itemState) {
      $logger.error('Product state: No state found for active item', { activeItemSessionId: activeItemSessionId.value })
      return
    }

    const step = itemState.currentStep
    const assessment = itemState.assessment

    if (step === 1) {
      if (assessment.productState === 'in-box') {
        // This is the final step - no navigation needed
        return
      } else {
        itemState.currentStep = 2
      }
    } else if (step === 2) {
      if (assessment.assemblyState !== 'assembled') {
        // This is the final step - no navigation needed
        return
      } else {
        itemState.currentStep = 3
      }
    } else if (step === 3) {
      if (assessment.canDisassemble === 'yes') {
        // Can be put back in original packaging - this is the final step
        return
      } else {
        // Any non-'yes' answer ('no', or a legacy 'partially' persisted on an
        // in-progress/restored session before PEG-853 dropped that option) goes
        // to the dimensions step. Using an else here (not `=== 'no'`) ensures a
        // stranded legacy value still advances instead of stalling on step 3.
        itemState.currentStep = 4
      }
    }
    // Step 4 is the final step when reached
  }

  /**
   * Navigate to previous step based on assessment history
   * Logic: Determine previous step based on current state
   * @public
   */
  function prevStep() {
    if (!activeItemSessionId.value) {
      $logger.warn('Product state: Cannot navigate without active item')
      return
    }

    const itemState = productStateAssessments.value[activeItemSessionId.value]
    if (!itemState) {
      $logger.error('Product state: No state found for active item', { activeItemSessionId: activeItemSessionId.value })
      return
    }

    const step = itemState.currentStep

    if (step === 4) {
      itemState.assessment.assembledDimensions = null
      itemState.currentStep = 3
    } else if (step === 3) {
      itemState.assessment.canDisassemble = null
      itemState.currentStep = 2
    } else if (step === 2) {
      itemState.assessment.assemblyState = null
      itemState.currentStep = 1
    }
    // Step 1 has no previous step
  }

  /**
   * Set the current step directly (used for browser back/forward sync)
   * @param {number} step - Step number to set
   * @public
   */
  function setCurrentStep(step) {
    if (!activeItemSessionId.value) {
      $logger.warn('Product state: Cannot set step without active item')
      return
    }

    const itemState = productStateAssessments.value[activeItemSessionId.value]
    if (!itemState) {
      $logger.warn('Product state: No state found for active item')
      return
    }

    const oldStep = itemState.currentStep

    // When navigating backwards, reset the answer(s) for the step(s) being left
    if (step < oldStep) {
      if (oldStep >= 4) itemState.assessment.assembledDimensions = null
      if (oldStep >= 3 && step < 3) itemState.assessment.canDisassemble = null
      if (oldStep >= 2 && step < 2) itemState.assessment.assemblyState = null
    }

    itemState.currentStep = step
  }

  /**
   * Reset assessment to initial state
   * @param {string} [lineItemSessionId] - Optional: Reset specific item, or all if omitted
   * @public
   */
  function reset(lineItemSessionId) {
    if (lineItemSessionId) {
      // Reset specific item
      if (productStateAssessments.value[lineItemSessionId]) {
        productStateAssessments.value[lineItemSessionId] = {
          assessment: { ...DEFAULT_PRODUCT_STATE_ASSESSMENT },
          currentStep: 1,
          isComplete: false,
        }
      }
    } else {
      // Reset all
      productStateAssessments.value = {}
      activeItemSessionId.value = null
    }
  }

  /**
   * Submit the assessment form and build formatted data
   * Saves to active item's state
   * @param {ProductStateAssessment} answers - Assessment answers
   * @returns {Object} Formatted submission data
   * @public
   */
  function submitForm(answers) {
    if (!activeItemSessionId.value) {
      $logger.error('Product state: Cannot submit without active item')
      return null
    }

    const itemState = productStateAssessments.value[activeItemSessionId.value]
    if (!itemState) {
      $logger.error('Product state: No state found for active item', { activeItemSessionId: activeItemSessionId.value })
      return null
    }

    // Update the item's assessment with submitted answers
    itemState.assessment = { ...answers }
    itemState.isComplete = true

    const formData = {
      productState: answers.productState,
      removedFromPackaging: answers.productState === 'removed',
      ...(answers.productState === 'removed' && {
        assemblyState: answers.assemblyState,
        wasAssembled: answers.assemblyState === 'assembled',
      }),
      ...(answers.assemblyState === 'assembled' && {
        canDisassemble: answers.canDisassemble,
      }),
      ...(answers.assembledDimensions && {
        assembledDimensions: answers.assembledDimensions,
      }),
    }

    return formData
  }

  // ============================================================================
  // PUBLIC API
  // ============================================================================

  return {
    // State (computed for active item)
    currentStep,
    totalSteps,
    productStateAssessment,
    activeItemSessionId,
    productStateAssessments, // Exposed for direct component access
    // Actions
    setActiveItem,
    initializeItemAssessment,
    getAssessmentForItem,
    isItemAssessmentComplete,
    reset,
    nextStep,
    prevStep,
    setCurrentStep,
    submitForm,
  }
})
